import { cn } from "@/lib/utils";
import { Sparkline } from "./Sparkline";
import { useAnimatedNumber } from "./AnimatedNumber";

interface CounterTileProps {
  label: string;
  value: number;
  /** Values for the inline sparkline, oldest → newest. */
  sparklineValues?: number[];
  /** Suffix shown after the animated value (e.g. "columns"). */
  unit?: string;
  /** Format for the big number. "compact" → 1.24M / 24.8K. "full" → 1,247,592. */
  format?: "compact" | "full";
  /** Total animation duration in ms. */
  durationMs?: number;
  className?: string;
  ariaLabel?: string;
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return Math.round(n).toLocaleString();
}

function formatFull(n: number): string {
  return Math.round(n).toLocaleString();
}

/**
 * Counter tile with count-up on mount and smooth transition on value change.
 *
 * Animation tweens from the previously-displayed value rather than restarting
 * from 0, so filter-driven updates feel responsive rather than jarring.
 * Respects prefers-reduced-motion (the hook gates it).
 */
export function CounterTile({
  label,
  value,
  sparklineValues,
  unit,
  format = "compact",
  durationMs = 1400,
  className,
  ariaLabel,
}: CounterTileProps) {
  const displayed = useAnimatedNumber(value, durationMs);
  const formatted = format === "compact" ? formatCompact(displayed) : formatFull(displayed);

  return (
    <div
      className={cn(
        "bg-white border border-[var(--color-border-light)] rounded-[10px] px-4 py-4",
        "flex flex-col gap-2",
        className
      )}
      aria-label={ariaLabel ?? `${label}: ${formatFull(value)}${unit ? ` ${unit}` : ""}`}
    >
      <div className="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] font-medium">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <div
          className="text-[26px] font-semibold leading-none tracking-tight text-[var(--color-text-primary)]"
          style={{ fontFeatureSettings: '"tnum"' }}
        >
          {formatted}
        </div>
        {unit && <div className="text-[11px] text-[var(--color-text-tertiary)]">{unit}</div>}
      </div>
      {sparklineValues && sparklineValues.length > 1 && (
        <Sparkline
          values={sparklineValues}
          stroke="var(--color-brand-primary-dark)"
          fill="var(--color-brand-primary)"
          height={28}
          className="mt-1"
        />
      )}
    </div>
  );
}
